import { useState } from 'react';
import { useNotificationBar } from 'context/notificationContext';
import { ContinueWatchingConfig } from 'interfaces/widget-types';
import Tooltip from 'components/styledFormElements/Tooltip';

interface Props {
    widget: {
        value: string;
        label: string;
        code: string;
    };
    initialData?: any;
    onSave: (payload: ContinueWatchingConfig | null) => void;
    onCancel?: () => void;
}

const ContinueWatching = ({ widget, initialData, onSave, onCancel }: Props) => {
    const notification = useNotificationBar();

    // Extract data from the nested structure
    const widgetData = initialData?.data || initialData;


    const [completionPercentage, setCompletionPercentage] = useState(
        widgetData?.completion_percentage || ''
    );
    const [maxItems, setMaxItems] = useState(
        widgetData?.max_items || ''
    );
    const [sorting, setSorting] = useState(
        widgetData?.sorting || ''
    );

    // Validation error states
    const [errors, setErrors] = useState({
        completionPercentage: '',
        maxItems: '',
        sorting: ''
    });

    const handleSave = async () => {
        const newErrors = {
            completionPercentage: '',
            maxItems: '',
            sorting: ''
        };

        // Validate completion percentage
        if (completionPercentage === '' || completionPercentage === null || completionPercentage === undefined) {
            newErrors.completionPercentage = 'Completion percentage is required';
        } else if (Number(completionPercentage) < 0 || Number(completionPercentage) > 100) {
            newErrors.completionPercentage = 'Completion percentage must be between 0 and 100';
        }

        // Validate max items
        if (maxItems === '' || maxItems === null || maxItems === undefined) {
            newErrors.maxItems = 'Max items is required';
        } else if (Number(maxItems) < 1 || Number(maxItems) > 12) {
            newErrors.maxItems = 'Max items must be between 1 and 12';
        }

        // Validate sorting
        if (!sorting || sorting === '') {
            newErrors.sorting = 'Sorting is required';
        }

        // Set errors and stop if there are any
        if (newErrors.completionPercentage || newErrors.maxItems || newErrors.sorting) {
            setErrors(newErrors);
            return;
        }

        // Clear errors if validation passes
        setErrors({
            completionPercentage: '',
            maxItems: '',
            sorting: ''
        });

        const data: any = {
            completion_percentage: completionPercentage,
            max_items: maxItems,
            sorting: sorting,
            widget_title: widget.label,
            widget_code: widget.code,
            widget_type: Number(widget.value),
            view_all: true,
            source_id: "",
            source_type: "DYNAMIC",
            widget_icon: "widgets/images/generate_1768977094144_12d19469.svg",
            content_rule: "AUTOMATIC",
            content_type: "CONTEXTUAL",
        };

        const finalData: any = {
            name: widget.label,
            widget_code: widget.code,
            widget_type: Number(widget.value),
            data
        };
        onSave(finalData);

        // ✅ SUCCESS NOTIFICATION
        notification.push({
            title: 'Success',
            message: 'Continue Watching widget saved successfully',
            type: 'success',
        })
    };

    // Real-time validation functions
    const validateCompletionPercentage = (value: string | number) => {
        if (value === '' || value === null || value === undefined) {
            return 'Completion percentage is required';
        }
        const numValue = Number(value);
        if (numValue < 0 || numValue > 100) {
            return 'Completion percentage must be between 0 and 100';
        }
        return '';
    };

    const validateMaxItems = (value: string | number) => {
        if (value === '' || value === null || value === undefined) {
            return 'Max items is required';
        }
        const numValue = Number(value);
        if (numValue < 1 || numValue > 12) {
            return 'Max items must be between 1 and 12';
        }
        return '';
    };

    const validateSorting = (value: string) => {
        if (!value || value === '') {
            return 'Sorting is required';
        }
        return '';
    };

    // Field change handlers with validation
    const handleCompletionPercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value === '' ? '' : Number(e.target.value);
        setCompletionPercentage(value);
        setErrors(prev => ({ ...prev, completionPercentage: validateCompletionPercentage(value) }));
    };

    const handleMaxItemsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value === '' ? '' : Number(e.target.value);
        setMaxItems(value);
        setErrors(prev => ({ ...prev, maxItems: validateMaxItems(value) }));
    };

    const handleSortingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        setSorting(value);
        setErrors(prev => ({ ...prev, sorting: validateSorting(value) }));
    };

    const handleCancel = () => {
        if (initialData) {
            onSave(initialData);
        } else {
            onSave(null);
        }
    };

    // Generate options for max items dropdown
    const maxItemsOptions = Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: `${i + 1}`
    }));

    const sortingOptions = [
        { value: 'sort_by_recency', label: 'Sort shows by recency of watch date' },
        { value: 'latest_first', label: 'Latest first' },
    ];

    return (
        <div className="space-y-6">

            {/* Qualification Configuration */}
            <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Shows only qualify for continue watching if the user has completed at least X% of the show
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                    <div className='flex flex-col gap-1'>
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Completion Percentage <span className="text-red-500">*</span>
                            </label>
                            <Tooltip
                                items={[
                                    'Minimum percentage a user must complete for a show to appear in Continue Watching',
                                    'Range: 0-100%'
                                ]}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={completionPercentage}
                                onChange={handleCompletionPercentageChange}
                                className={`w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500 ${
                                    errors.completionPercentage ? 'border-red-500' : ''
                                }`}
                                placeholder="Enter completion percentage"
                            />
                            <span className="text-sm text-gray-600 dark:text-gray-400">%</span>
                        </div>
                        {errors.completionPercentage && (
                            <p className="text-xs text-red-500">{errors.completionPercentage}</p>
                        )}
                        <p className="text-xs text-gray-500">Minimum percentage completed to qualify (0-100)</p>
                    </div>
                </div>
            </div>

            {/* List Configuration */}
            <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                    <div className='flex flex-col gap-1'>
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Max Items in List <span className="text-red-500">*</span>
                            </label>
                            <Tooltip
                                items={[
                                    'Maximum number of shows to display in the Continue Watching widget',
                                    'Range: 1-12 shows'
                                ]}
                            />
                        </div>
                        <select
                            value={maxItems}
                            onChange={handleMaxItemsChange}
                            className={`w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500 ${
                                errors.maxItems ? 'border-red-500' : ''
                            }`}
                        >
                            <option value="">Select max items</option>
                            {maxItemsOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {errors.maxItems && (
                            <p className="text-xs text-red-500">{errors.maxItems}</p>
                        )}
                        <p className="text-xs text-gray-500">Maximum number of shows to display (1-12)</p>
                    </div>

                    <div className='flex flex-col gap-1'>
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Sorting <span className="text-red-500">*</span>
                            </label>
                            <Tooltip
                                items={[
                                    'Determines the order in which shows appear in Continue Watching',
                                    'Sort by recency: Most recently watched shows appear first',
                                    'Latest first: Newest episodes/shows appear first'
                                ]}
                            />
                        </div>
                        <select
                            value={sorting}
                            onChange={handleSortingChange}
                            className={`w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white dark:placeholder-gray-500 ${
                                errors.sorting ? 'border-red-500' : ''
                            }`}
                        >
                            <option value="">Select sorting</option>
                            {sortingOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {errors.sorting && (
                            <p className="text-xs text-red-500">{errors.sorting}</p>
                        )}
                        <p className="text-xs text-gray-500">Sort shows by recency of watch date (latest first)</p>
                    </div>
                </div>
            </div>
            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-slate-600">
                <button
                    onClick={handleCancel}
                    className="px-6 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Save
                </button>
            </div>
        </div>
    );
};

export default ContinueWatching;
